import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Calendar, Award, ArrowRight, Droplets, Activity } from 'lucide-react';

const Home = () => {
  const impactNumbers = [
    { number: '10,000+', label: 'Lives Saved', icon: Heart, color: 'text-red-500' },
    { number: '5,000+', label: 'Blood Donors', icon: Droplets, color: 'text-blue-500' },
    { number: '500+', label: 'Events Organized', icon: Calendar, color: 'text-green-500' },
    { number: '100+', label: 'Cities Covered', icon: Users, color: 'text-purple-500' },
  ];

  const features = [
    {
      title: 'Blood Donor Registry',
      description: 'Connect with donors quickly in emergency situations. Our comprehensive registry helps save lives.',
      icon: Droplets,
      link: '/blood-donor-registry'
    },
    {
      title: 'Health Camps & Events',
      description: 'Participate in health awareness programs, blood donation camps, and community workshops.',
      icon: Calendar,
      link: '/events'
    },
    {
      title: 'Make a Difference',
      description: 'Support our mission through donations and help us expand our life-saving programs.',
      icon: Heart,
      link: '/donate'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-800 to-orange-600 text-white">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                  <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/aim-logo.png" 
              alt="AIM Foundation Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Together for Health,
            <span className="block text-yellow-300">Hope & Education</span>
          </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Join AIM Foundation in our mission to bridge India and Africa through comprehensive health initiatives, educational programs, and community development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center"
              >
                Join Our Mission
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="border-2 border-white hover:bg-white hover:text-green-800 px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>



      {/* Features Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              What We Do
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Our comprehensive approach to community health and blood donation
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg hover:shadow-lg transition-shadow duration-200">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 mb-4">
              <feature.icon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {feature.description}
                </p>
                <Link
                  to={feature.link}
                  className="inline-flex items-center text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium"
                >
                  Learn More
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Every action counts. Join thousands of volunteers and supporters who are building stronger communities every day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white hover:bg-gray-100 text-green-800 px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Become a Donor
            </Link>
            <Link
              to="/donate"
              className="border-2 border-white hover:bg-white hover:text-green-800 px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Support Our Cause
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
